import { loadScript } from '@mbrtargeting/metatag-utils';
import { logger } from '../../../loader/essentials/logger.js';

const log = logger.logGroup({ prefix: 'Adserver' });

declare var window: Window & {
    googletag: googletag.Googletag;
};

window.googletag = window.googletag || {};
window.googletag.cmd = window.googletag.cmd || [];

export const sendToGpt = async <T>(command: (googletag: googletag.Googletag, pubads: googletag.PubAdsService) => T) => new Promise<T>((resolve, reject) => {
    window.googletag.cmd.push(() => {
        try {
            const gpt = window.googletag; // get a fresh reference to googletag
            resolve(command(gpt, gpt.pubads()));
        } catch (e) {
            reject(e);
        }
    });
});

export const gptReady = async () => sendToGpt((gpt, pubads) => ({ gpt, pubads }));

export const gptLoadScript = async (): Promise<void> => {
    if (window.googletag?.apiReady) return log.notice('gpt.js already present');
    loadScript({
        src: 'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
        node: document.head,
        onload: () => log.notice('gpt.js loaded'),
        onerror: () => log.error('gpt.js could not be loaded'),
    });
};

export interface GptDefineSlotParams {
    logPrefix: string;
    adUnitPath: string;
    sizes: googletag.GeneralSize;
    divId?: string;
}

export const gptDefineSlot = async ({ logPrefix, adUnitPath, sizes, divId }: GptDefineSlotParams) => sendToGpt(gpt => {
    log.debug(`${logPrefix} Set up as standard gpt slot.`);
    const gptSlot = gpt.defineSlot(adUnitPath, sizes, divId);
    if (!gptSlot) throw new Error(`${logPrefix} Error creating standard gpt slot (adUnitPath=${adUnitPath}, divId=${divId}, sizes=${sizes})`);
    return gptSlot;
});

export interface GptDefineOutOfPageSlotParams {
    logPrefix: string;
    adUnitPath: string;
    divId?: string;
    outOfPageFormat?: googletag.enums.OutOfPageFormat;
}

export const gptDefineOutOfPageSlot = async ({ logPrefix, adUnitPath, divId, outOfPageFormat }: GptDefineOutOfPageSlotParams) => sendToGpt(gpt => {
    log.debug(`${logPrefix} Set up as out-of-page gpt slot.`);
    const gptSlot = gpt.defineOutOfPageSlot(adUnitPath, outOfPageFormat || divId);
    if (!gptSlot) throw new Error(`${logPrefix} Error creating out-of-page gpt slot (adUnitPath=${adUnitPath}, divId=${divId})`);
    return gptSlot;
});

export const gptDestroySlots = async (gptSlots: googletag.Slot[]) => sendToGpt(gpt => {
    return gptSlots.length > 0 ? gpt.destroySlots(gptSlots) : false;
});
